import React, { useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import ModuleFrame, { Chip, KV, useModuleTab } from '../../components/ModuleFrame';
import { AppFocusChips, useSelectedApp, useAppFocusSet, inFocus, withApp } from '../../components/AppSelector';
import { createEntity, deleteEntity, updateEntity, useEntityList, friendlyError } from '../../data/entities';
import { useCan } from '../../data/permissions';
import { apiPost, apiPut, apiDelete } from '../../utils/backendApi';
import { useAuth } from '../../AuthContext';
import ConnectorEditor, { NewConnector, ProviderCards, SyncChip, useConnectorProviders } from '../../components/ConnectorEditor';
import { LibraryLifecycle } from '../../components/LifecycleRail';

// Integrations & Endpoints — the external systems an app's workflows call.
// The secret itself never lands here: `secretKey` names an SSM parameter
// under pipeline-secrets/; the VALUE is pasted into "Secret value" and goes
// straight to SSM via PUT /integrations/:org/:id/secret (self-serve since
// 2026-10-12 — a partner had nowhere to put his API key). The backend
// resolves it when testing or when a pipeline capability fires. Triggers are the
// workflow's input.http / webhook blocks; audit comes from the commits
// ledger the runner writes on every side effect.

const KINDS = [
  ['http', 'REST / JSON API', 'Read or write a system over HTTPS — CRM, ticketing, ERP, internal services.'],
  ['webhook', 'Outgoing webhook', 'POST workflow results to a URL you own (output.webhook capability).'],
  ['notification', 'Notification', 'Email (SMTP seam) or chat webhook — Slack, Teams.'],
  ['knowledge', 'Knowledge connector', 'Sync documents from SharePoint, Confluence, Google Drive, Notion, an S3 bucket or a website into Knowledge on a schedule.'],
  ['s3', 'S3 bucket (another account)', 'Keys for a bucket outside this platform\'s account — nQube\'s S3 listing / Write to S3 blocks pick it by name and pull or push objects there.'],
];
const REGIONS = ['us-east-1', 'us-east-2', 'us-west-1', 'us-west-2', 'ca-central-1', 'eu-west-1', 'eu-west-2', 'eu-central-1', 'eu-north-1', 'ap-south-1', 'ap-southeast-1', 'ap-southeast-2', 'ap-northeast-1', 'sa-east-1'];
const AUTHS = ['none', 'bearer', 'apiKey', 'oauth2', 'sigv4'];
const SCOPES = ['sandbox', 'uat', 'prod'];

export default function IntegrationsModule() {
  const { org, myRole } = useOutletContext();
  const { user } = useAuth();
  const can = useCan(org, myRole);
  const isAdmin = can('integration.update'); // connect / edit / test / remove (default admin)
  const { app } = useSelectedApp();
  const { focus } = useAppFocusSet();
  const [tab, setTab] = useModuleTab('endpoints');
  const { docs: all } = useEntityList('integration', { org: org.id });
  const { docs: commits } = useEntityList('commit', { org: org.id });
  const [adding, setAdding] = useState(null); // kind, or 'knowledge:<provider>'
  const [selected, setSelected] = useState(null);
  const providers = useConnectorProviders(org.id);

  // With a focus: the systems linked to the apps in focus, then the shared
  // ones (linked to no app). Without: everything.
  const linkedTo = (all || []).filter((i) => focus && inFocus(focus, i.appIds));
  const shared = (all || []).filter((i) => !(i.appIds || []).length);
  const integrations = focus ? [...linkedTo, ...shared] : (all || []);
  const current = integrations.find((i) => i.id === selected) || integrations[0] || null;
  const failing = (all || []).filter((i) => i.lastTest && !i.lastTest.ok).length;
  const recentCommits = (commits || []).slice(0, 25);

  return (
    <ModuleFrame
      title="Integrations"
      subtitle="Endpoints · Catalog · Triggers · Audit"
      accent="#34d399"
      note={`Integrations, tab ${tab}${app ? `, app "${app.name}"` : ''}: ${(all || []).length} connected${failing ? `, ${failing} failing` : ''}. NoVA: create_integration (secret names only — values are pasted on the row), test_integration, list_integration; writes land in the commits ledger.`}
      help="Connect the systems the app reads from and writes to. Secrets live in the platform's secret store, never in this console; every write a workflow performs lands in the commits ledger."
      context={(
        <>
          <AppFocusChips multi />
          <Chip tone={failing ? 'danger' : (all || []).length ? 'ok' : 'muted'}>{(all || []).length} connected{failing ? ` · ${failing} failing` : ''}</Chip>
        </>
      )}
      lead={app ? <LibraryLifecycle org={org} app={app} moduleId="integrations" /> : null}
      tabs={[
        { id: 'endpoints', label: 'Endpoints', count: integrations.length },
        { id: 'catalog', label: 'Catalog' },
        { id: 'triggers', label: 'Triggers' },
        { id: 'audit', label: 'Audit', count: recentCommits.length },
      ]}
      tab={tab}
      onTab={setTab}
      verbs={[
        { label: '+ Connect a system', hint: isAdmin ? 'admin' : 'admins only', onClick: () => { setTab('catalog'); }, primary: true, disabled: !isAdmin },
        { label: 'Test all', hint: 'HEAD/GET each endpoint', onClick: () => Promise.all((all || []).map((i) => apiPost(`/integrations/${org.id}/${i.id}/test`).catch(() => {}))), disabled: !isAdmin || !(all || []).length },
        { label: 'Use in a workflow', hint: 'nQube capabilities', to: withApp(`/orgs/${org.id}/workflows`, app) },
      ]}
    >
      {tab === 'catalog' && (
        <div className="mf-grid-cards">
          {KINDS.map(([kind, label, help]) => (
            <div key={kind} className="mf-card" style={kind === 'knowledge' ? { gridColumn: '1 / -1' } : undefined}>
              <div className="mf-card-title">{label}</div>
              <div className="mf-muted" style={{ flex: 1 }}>{help}</div>
              {kind === 'knowledge' ? (
                <ProviderCards providers={providers} canConnect={isAdmin} onPick={(k) => { setAdding(`knowledge:${k}`); setTab('endpoints'); }} />
              ) : (
                <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" disabled={!isAdmin} onClick={() => { setAdding(kind); setTab('endpoints'); }}>Connect</button>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'endpoints' && (
        <ModuleFrame.Columns>
          <ModuleFrame.Panel title="Connected" meta={focus ? `${linkedTo.length} linked to ${focus.length > 1 ? `${focus.length} apps` : app.name} · ${shared.length} shared` : `${(all || []).length} in this organization`}>
            {adding && adding.startsWith('knowledge:') && providers && <NewConnector orgId={org.id} uid={user.uid} provider={adding.slice(10)} providers={providers} appId={app?.id} onDone={(id) => { setAdding(null); if (id) setSelected(id); }} />}
            {adding && !adding.startsWith('knowledge:') && <NewIntegration orgId={org.id} uid={user.uid} kind={adding} appId={app?.id} onDone={(id) => { setAdding(null); if (id) setSelected(id); }} />}
            {!integrations.length && !adding && <p className="cs-help">Nothing connected yet. {isAdmin ? <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" onClick={() => setTab('catalog')}>Open the catalog</button> : 'An admin can connect systems.'}</p>}
            <ul className="mf-list">
              {integrations.map((i, idx) => (
                <li key={i.id}>
                  {focus && idx === linkedTo.length && <span className="mf-muted" style={{ display: 'block', fontSize: 11, margin: '6px 0 2px' }}>Shared — not linked to an app</span>}
                  <button type="button" className="mf-row" style={{ width: '100%', cursor: 'pointer', color: 'var(--text)', borderColor: current?.id === i.id ? 'rgba(52,211,153,0.5)' : undefined }} onClick={() => setSelected(i.id)}>
                    {i.kind === 'knowledge' ? <SyncChip integ={i} /> : <Chip tone={!i.enabled ? 'muted' : i.lastTest?.ok ? 'ok' : i.lastTest ? 'danger' : 'muted'}>{!i.enabled ? 'off' : i.lastTest ? (i.lastTest.ok ? 'ok' : 'fail') : 'untested'}</Chip>}
                    <span className="mf-row-grow" style={{ textAlign: 'left' }}><strong>{i.name}</strong> <span className="mf-muted">{i.kind === 'knowledge' ? `${providers?.[i.provider]?.label || i.provider} · ${i.schedule || 'daily'}` : `${i.kind} · ${i.scope}`}</span></span>
                  </button>
                </li>
              ))}
            </ul>
          </ModuleFrame.Panel>
            <ModuleFrame.Panel title={current ? current.name : 'Endpoint'} meta={current ? (current.kind === 'knowledge' ? 'knowledge connector — synced documents land in Knowledge' : current.kind === 's3' ? `s3://${current.config?.bucket || '…'}${current.config?.prefix ? `/${current.config.prefix}` : ''} · ${current.config?.region || 'region?'}` : current.baseUrl) : 'select an endpoint'} className="mf-span-2">
            {current && current.kind === 'knowledge' && <ConnectorEditor orgId={org.id} integ={current} providers={providers} canEdit={isAdmin} app={app} />}
            {current && current.kind !== 'knowledge' && <EndpointEditor orgId={org.id} integ={current} isAdmin={isAdmin} app={app} />}
          </ModuleFrame.Panel>
        </ModuleFrame.Columns>
      )}

      {tab === 'triggers' && (
        <ModuleFrame.Columns cols={2}>
          <ModuleFrame.Panel title="Inbound" meta="how work enters a workflow">
            <KV rows={[
              ['User request', 'NoVA channels (web, widget, voice, mobile) call the app\'s answer path directly'],
              ['HTTP fetch', 'an input.http block polls a public URL (streaming: pollSeconds)'],
              ['Dataset', 'input.dataset reads files dropped into a Listen dataset'],
              ['Schedule', 'streaming pipelines run always-on; batch runs start from the canvas or Processes'],
            ]} />
          </ModuleFrame.Panel>
          <ModuleFrame.Panel title="Outbound (actions)" meta="each side effect names a pipeline capability">
            <KV rows={[
              ['output.webhook', 'POST to an integration\'s https URL; secret from pipeline-secrets/<secretKey>'],
              ['output.knowledge', 'write a knowledge doc (then approve + publish in Knowledge)'],
              ['output.dataset / s3write', 'files to a dataset or the public bucket'],
              ['Gate rule', 'a model block cannot reach a side effect without control.review or control.filter in between'],
            ]} />
            <Link to={withApp(`/orgs/${org.id}/workflows`, app)} className="mf-muted">Wire them on the canvas →</Link>
          </ModuleFrame.Panel>
        </ModuleFrame.Columns>
      )}

      {tab === 'audit' && (
        <ModuleFrame.Panel title="Commits ledger" meta="every side effect a workflow performed — server-written, immutable">
          {!recentCommits.length && <p className="cs-help">No side effects recorded yet.</p>}
          <table className="mf-table">
            <thead><tr><th>When</th><th>Capability</th><th>Block</th><th>Pipeline</th><th>Review</th><th>Payload hash</th></tr></thead>
            <tbody>
              {recentCommits.map((c) => (
                <tr key={c.id}>
                  <td className="mf-muted">{c.createdAt ? new Date(c.createdAt).toLocaleString() : '—'}</td>
                  <td>{c.capabilityKind} <span className="mf-muted">{c.capabilityId}</span></td>
                  <td>{c.blockType}</td>
                  <td className="mf-muted">{c.pipelineId}</td>
                  <td>{c.reviewId ? <Chip tone="ok">reviewed</Chip> : <Chip tone="muted">filter</Chip>}</td>
                  <td className="mf-muted" style={{ fontFamily: 'ui-monospace, monospace' }}>{(c.payloadHash || '').slice(0, 12)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ModuleFrame.Panel>
      )}
    </ModuleFrame>
  );
}

function NewIntegration({ orgId, uid, kind, appId, onDone }) {
  const [name, setName] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [bucket, setBucket] = useState('');
  const [region, setRegion] = useState('us-east-1');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const isS3 = kind === 's3';
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      // An S3 bucket in another account starts with access-key auth (sigv4:
      // Access key ID + secret access key); switch Auth to none for a bucket
      // whose policy already admits the platform role.
      const extra = isS3 ? { auth: 'sigv4', config: { bucket: bucket.trim().toLowerCase(), region, prefix: '' } } : {};
      const id = await createEntity('integration', { org: orgId }, { name: name.trim(), kind, baseUrl: baseUrl.trim(), appIds: appId ? [appId] : [], createdBy: uid, ...extra });
      onDone(id);
    } catch (ex) { setErr(friendlyError(ex)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="mf-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 6, marginBottom: 8 }}>
      <strong>New {isS3 ? 'S3 bucket' : kind}</strong>
      <input className="cs-input" value={name} onChange={(e) => setName(e.target.value)} placeholder={isS3 ? 'Name — e.g. Client data lake' : 'Name — e.g. Service API'} aria-label="Name" />
      {isS3 ? (
        <div style={{ display: 'flex', gap: 6 }}>
          <input className="cs-input" style={{ flex: 2 }} value={bucket} onChange={(e) => setBucket(e.target.value)} placeholder="bucket name" aria-label="Bucket" />
          <select className="cs-input" style={{ flex: 1 }} value={region} onChange={(e) => setRegion(e.target.value)} aria-label="Region">{REGIONS.map((r) => <option key={r}>{r}</option>)}</select>
        </div>
      ) : (
        <input className="cs-input" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder="https://…" aria-label="Base URL" />
      )}
      {err && <p className="cs-form-error" role="alert" style={{ margin: 0 }}>{err}</p>}
      <div style={{ display: 'flex', gap: 6 }}>
        <button type="submit" className="cs-btn cs-btn-sm cs-btn-primary" disabled={busy || !name.trim() || (isS3 && !bucket.trim())}>Create</button>
        <button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" onClick={() => onDone(null)}>Cancel</button>
      </div>
    </form>
  );
}

function EndpointEditor({ orgId, integ, isAdmin, app }) {
  const [form, setForm] = useState(integ);
  const [state, setState] = useState('');
  const [testing, setTesting] = useState(false);
  const [secret, setSecret] = useState('');
  const [sessionToken, setSessionToken] = useState('');
  const [secretState, setSecretState] = useState('');
  React.useEffect(() => { setForm(integ); setState(''); setSecret(''); setSessionToken(''); setSecretState(''); }, [integ]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  // The secret value never touches Firestore: PUT to the backend, which
  // writes SSM pipeline-secrets/<secretKey>. Save the key NAME first.
  const storeSecret = async () => {
    setSecretState('storing');
    try {
      if ((form.secretKey || '') !== (integ.secretKey || '')) throw new Error('save the integration first so the secret key name is on record');
      const value = form.auth === 'sigv4' && sessionToken.trim() ? `${secret.trim()}\n${sessionToken.trim()}` : secret;
      await apiPut(`/integrations/${orgId}/${integ.id}/secret`, { value });
      setSecret('');
      setSessionToken('');
      setSecretState('stored');
    } catch (e) { setSecretState(e.message); }
  };
  const clearSecret = async () => {
    if (!window.confirm('Remove the stored secret? Workflows calling this system will fail until a new one is stored.')) return;
    setSecretState('storing');
    try { await apiDelete(`/integrations/${orgId}/${integ.id}/secret`); setSecretState('cleared'); } catch (e) { setSecretState(e.message); }
  };
  const save = async () => {
    setState('saving');
    try {
      const { id, lastTest, secretSetAt, createdAt, createdBy, updatedAt, ...rest } = form;
      await updateEntity('integration', { org: orgId }, integ.id, rest);
      setState('saved');
    } catch (e) { setState(friendlyError(e)); }
  };
  const test = async () => {
    setTesting(true);
    try { await apiPost(`/integrations/${orgId}/${integ.id}/test`); } catch (e) { setState(e.message); } finally { setTesting(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Remove ${integ.name}?`)) return;
    await deleteEntity('integration', { org: orgId }, integ.id).catch((e) => setState(friendlyError(e)));
  };
  const linked = app && (form.appIds || []).includes(app.id);
  const actions = Array.isArray(form.actions) ? form.actions : [];
  const isS3 = form.kind === 's3';
  const cfg = form.config || {};
  const setCfg = (k, v) => set('config', { ...cfg, [k]: v });
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {integ.lastTest ? (
          <Chip tone={integ.lastTest.ok ? 'ok' : 'danger'} title={integ.lastTest.detail || integ.lastTest.error || ''}>
            {integ.lastTest.ok
              ? (integ.lastTest.status === 400 ? `credentials accepted · HTTP 400 · ${integ.lastTest.ms} ms` : `reachable · HTTP ${integ.lastTest.status} · ${integ.lastTest.ms} ms`)
              : `failed · ${integ.lastTest.error || `HTTP ${integ.lastTest.status}`}`}
          </Chip>
        ) : <Chip tone="muted">never tested</Chip>}
        <span className="mf-muted">{integ.lastTest?.at ? new Date(integ.lastTest.at).toLocaleString() : ''}</span>
        <span style={{ flex: 1 }} />
        <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" disabled={!isAdmin || testing} onClick={test}>{testing ? 'Testing…' : 'Test connection'}</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <label className="mf-muted">Name<input className="cs-input" value={form.name || ''} onChange={(e) => set('name', e.target.value)} disabled={!isAdmin} /></label>
        <label className="mf-muted">Kind<select className="cs-input" value={form.kind || 'http'} onChange={(e) => set('kind', e.target.value)} disabled={!isAdmin}>{KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
        {isS3 ? (
          <>
            <label className="mf-muted">Bucket<input className="cs-input" value={cfg.bucket || ''} onChange={(e) => setCfg('bucket', e.target.value.trim().toLowerCase())} disabled={!isAdmin} placeholder="client-data-lake" /></label>
            <label className="mf-muted">Region<select className="cs-input" value={cfg.region || 'us-east-1'} onChange={(e) => setCfg('region', e.target.value)} disabled={!isAdmin}>{[...new Set([...REGIONS, cfg.region].filter(Boolean))].map((r) => <option key={r}>{r}</option>)}</select></label>
            <label className="mf-muted">Prefix <span title="Optional folder inside the bucket; blocks list and write under it.">(optional)</span><input className="cs-input" value={cfg.prefix || ''} onChange={(e) => setCfg('prefix', e.target.value.replace(/^\/+/, ''))} disabled={!isAdmin} placeholder="exports/2026/" /></label>
            <label className="mf-muted">Endpoint <span title="Only for S3-compatible storage (MinIO, Wasabi, R2). Leave empty for AWS.">(S3-compatible only)</span><input className="cs-input" value={cfg.endpoint || ''} onChange={(e) => setCfg('endpoint', e.target.value.trim())} disabled={!isAdmin} placeholder="https://…" /></label>
            <p className="mf-muted" style={{ gridColumn: 'span 2', margin: 0 }}>Auth <strong>sigv4</strong> = an IAM user's access key (Access key ID below, secret access key as the secret value). Auth <strong>none</strong> = the bucket's policy already admits this platform's role. nQube blocks: S3 listing and Write to S3 pick this connection by name.</p>
          </>
        ) : (
          <label className="mf-muted" style={{ gridColumn: 'span 2' }}>Base URL (https)<input className="cs-input" value={form.baseUrl || ''} onChange={(e) => set('baseUrl', e.target.value)} disabled={!isAdmin} /></label>
        )}
        <label className="mf-muted">Auth<select className="cs-input" value={form.auth || 'none'} onChange={(e) => set('auth', e.target.value)} disabled={!isAdmin}>{(isS3 ? ['none', 'sigv4'] : AUTHS).map((a) => <option key={a}>{a}</option>)}</select></label>
        <label className="mf-muted">Secret key <span title="SSM parameter name under /<slug>/pipeline-secrets/">(pipeline-secrets/…)</span><input className="cs-input" value={form.secretKey || ''} onChange={(e) => set('secretKey', e.target.value.replace(/[^A-Za-z0-9_-]/g, ''))} disabled={!isAdmin || form.auth === 'none'} placeholder="SERVICE_API" /></label>
        {form.auth !== 'none' && (
          <label className="mf-muted" style={{ gridColumn: 'span 2' }}>
            {form.auth === 'sigv4' ? 'Access key ID' : 'Access key ID / Client ID'} <span title={form.auth === 'sigv4' ? 'AWS access key id. Used in the SigV4 Authorization credential. Not a secret.' : 'AWS access key id, or the client id for another API. Saved on this endpoint and sent as the x-client-id header. Not a secret.'}>{form.auth === 'sigv4' ? '(signs the request — not a secret itself)' : '(pairs with the secret — not a secret itself)'}</span>
            <input className="cs-input" value={form.clientId || ''} onChange={(e) => set('clientId', e.target.value.replace(/[^A-Za-z0-9._@+-]/g, '').slice(0, 200))} disabled={!isAdmin} placeholder={form.auth === 'sigv4' ? 'AKIA…' : 'AKIA… or client id'} autoComplete="off" />
          </label>
        )}
        {form.auth !== 'none' && (
          <label className="mf-muted" style={{ gridColumn: 'span 2' }}>
            Secret value <span title="Pasted here, stored encrypted in the platform secret store under the key name above. Never shown again, never in this database.">{form.auth === 'sigv4' ? '(secret access key — stored encrypted, never shown again)' : '(secret access key, client secret, or token — stored encrypted, never shown again)'}</span>
            <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input className="cs-input" type="password" autoComplete="off" value={secret} onChange={(e) => setSecret(e.target.value)} disabled={!isAdmin || !integ.secretKey} placeholder={integ.secretKey ? (integ.secretSetAt ? 'paste a new value to replace the stored one' : (form.auth === 'sigv4' ? 'paste the secret access key' : 'paste the secret access key, client secret, or token')) : 'save a secret key name first'} style={{ flex: 1 }} />
              <button type="button" className="cs-btn cs-btn-sm cs-btn-primary" disabled={!isAdmin || !integ.secretKey || secret.trim().length < 4 || secretState === 'storing'} onClick={storeSecret}>{secretState === 'storing' ? 'Storing…' : 'Store'}</button>
              {integ.secretSetAt && <button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" disabled={!isAdmin || secretState === 'storing'} onClick={clearSecret}>Clear</button>}
            </span>
            {form.auth === 'sigv4' && (
              <input className="cs-input" type="password" autoComplete="off" value={sessionToken} onChange={(e) => setSessionToken(e.target.value)} disabled={!isAdmin || !integ.secretKey} placeholder="session token — only for temporary credentials" style={{ marginTop: 6 }} />
            )}
            <span style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
              {integ.secretSetAt ? <Chip tone="ok">secret stored · {new Date(integ.secretSetAt).toLocaleString()}</Chip> : <Chip tone="muted">no secret stored</Chip>}
              {secretState && !['storing', 'stored', 'cleared'].includes(secretState) && <span className="cs-form-error">{secretState}</span>}
              {secretState === 'stored' && <span className="mf-muted">Stored. Test the connection to confirm.</span>}
            </span>
          </label>
        )}
        <label className="mf-muted">Scope<select className="cs-input" value={form.scope || 'sandbox'} onChange={(e) => set('scope', e.target.value)} disabled={!isAdmin}>{SCOPES.map((s) => <option key={s}>{s}</option>)}</select></label>
        <label className="mf-toggle" style={{ alignSelf: 'end' }}><input type="checkbox" checked={form.enabled !== false} onChange={(e) => set('enabled', e.target.checked)} disabled={!isAdmin} /> Enabled</label>
      </div>
      {!isS3 && <div>
        <div className="mf-muted" style={{ marginBottom: 6 }}>Actions <span title="name + method + path; reference them from workflow blocks">(what workflows may call)</span></div>
        {form.auth === 'sigv4' && <p className="mf-muted" style={{ margin: '0 0 6px' }}>Each call is signed. Transcribe: path <code>/transcribe</code>, x-amz-target <code>com.amazonaws.transcribe.Transcribe.StartTranscriptionJob</code>. The request also sends content-type application/x-amz-json-1.1, x-amz-content-sha256, x-amz-date and authorization. A session token, when set, is sent as x-amz-security-token.</p>}
        <table className="mf-table">
          <thead><tr><th>Name</th><th>Method</th><th>Path</th>{form.auth === 'sigv4' && <th>x-amz-target</th>}<th /></tr></thead>
          <tbody>
            {actions.map((a, i) => (
              <tr key={i}>
                <td><input className="cs-input" value={a.name || ''} onChange={(e) => set('actions', actions.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} disabled={!isAdmin} /></td>
                <td><select className="cs-input" value={a.method || 'GET'} onChange={(e) => set('actions', actions.map((x, j) => (j === i ? { ...x, method: e.target.value } : x)))} disabled={!isAdmin}>{['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((m) => <option key={m}>{m}</option>)}</select></td>
                <td><input className="cs-input" value={a.path || ''} onChange={(e) => set('actions', actions.map((x, j) => (j === i ? { ...x, path: e.target.value } : x)))} disabled={!isAdmin} placeholder={form.auth === 'sigv4' ? '/transcribe' : '/tickets'} /></td>
                {form.auth === 'sigv4' && <td><input className="cs-input" value={a.amzTarget || ''} onChange={(e) => set('actions', actions.map((x, j) => (j === i ? { ...x, amzTarget: e.target.value } : x)))} disabled={!isAdmin} placeholder="com.amazonaws.transcribe.Transcribe.StartTranscriptionJob" /></td>}
                <td><button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" disabled={!isAdmin} onClick={() => set('actions', actions.filter((_, j) => j !== i))}>×</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" style={{ marginTop: 6 }} disabled={!isAdmin} onClick={() => set('actions', [...actions, { name: '', method: 'GET', path: '' }])}>+ Action</button>
      </div>}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {isAdmin && <button type="button" className="cs-btn cs-btn-sm cs-btn-primary" disabled={state === 'saving'} onClick={save}>{state === 'saving' ? 'Saving…' : 'Save'}</button>}
        {app && isAdmin && (
          <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" onClick={() => set('appIds', linked ? (form.appIds || []).filter((x) => x !== app.id) : [...(form.appIds || []), app.id])}>
            {linked ? `● used by ${app.name}` : `○ link to ${app.name}`}
          </button>
        )}
        {isAdmin && <button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" onClick={remove}>Remove</button>}
        {state && state !== 'saving' && <span className={state === 'saved' ? 'mf-muted' : 'cs-form-error'}>{state === 'saved' ? 'Saved.' : state}</span>}
      </div>
    </div>
  );
}
